/**
 * Reset Password Page
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useResetPassword } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

export function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const resetPassword = useResetPassword();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [validationError, setValidationError] = useState('');

    const token = searchParams.get('token') || '';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError('');

        if (password !== confirmPassword) {
            setValidationError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setValidationError('Password must be at least 8 characters');
            return;
        }

        resetPassword.mutate({ token, password });
    };

    if (!token) {
        return (
            <div className="space-y-4 text-center">
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        <h2 className="font-semibold">Invalid link</h2>
                        <p className="mt-1 text-sm">
                            This password reset link is invalid or has expired.
                        </p>
                    </AlertDescription>
                </Alert>
                <Link
                    to="/auth/forgot-password"
                    className="text-sm text-primary hover:underline"
                >
                    Request a new link
                </Link>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center">
                <h2 className="text-lg font-semibold text-foreground">Set new password</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    Enter your new password below.
                </p>
            </div>

            {(validationError || resetPassword.error) && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        {validationError || (resetPassword.error instanceof Error ? resetPassword.error.message : 'An error occurred')}
                    </AlertDescription>
                </Alert>
            )}

            <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="••••••••"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="••••••••"
                />
            </div>

            <Button
                type="submit"
                disabled={resetPassword.isPending}
                className="w-full"
            >
                {resetPassword.isPending ? 'Resetting...' : 'Reset password'}
            </Button>

            <div className="text-center text-sm">
                <Link
                    to="/auth/login"
                    className="text-primary hover:underline"
                >
                    Back to login
                </Link>
            </div>
        </form>
    );
}
